/**
 * ALDER SEARCH — Leadership Team Buildouts Page
 * URL: /executive-search/leadership-team-buildouts
 */

import { useEffect } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useFaqSchema } from "@/hooks/useFaqSchema";

const leadershipBuildoutsFaqs = [
  {
    question: "What is a product leadership team buildout in VC and PE?",
    answer: "A product leadership team buildout in VC and PE is the process of systematically assessing and strengthening the product organisation of a portfolio company, typically following acquisition or at a significant inflection point such as a platform acquisition or pre-exit preparation. It involves identifying capability gaps, defining the target leadership structure, and running multiple concurrent or sequential searches to fill those gaps.",
  },
  {
    question: "How do you approach a leadership team buildout for a VC- or PE-backed portfolio company?",
    answer: "We start with a product leadership audit that maps the existing team against the value creation plan requirements. We identify which product roles need to be strengthened, which may need to be replaced, and which new roles need to be created. We then define a sequenced search programme that prioritises the hires most critical to near-term value creation.",
  },
  {
    question: "How many searches can you run concurrently for a single portfolio company?",
    answer: "We can run multiple concurrent searches for a single portfolio company, typically up to three or four active mandates at any one time. Running searches concurrently requires careful coordination to avoid candidate overlap and to ensure each search receives appropriate resource. We recommend sequencing searches where possible to allow each new hire to influence the next.",
  },
  {
    question: "What is the difference between a leadership team buildout and a standard executive search?",
    answer: "A standard executive search fills a single role. A product leadership team buildout is a programme of work that addresses the full product structure of a business. It requires a deeper understanding of the business strategy, the existing team dynamics, and the sequencing of hires. The output is a stronger, more aligned product leadership team, not just a filled vacancy.",
  },
  {
    question: "How do you manage the confidentiality of a leadership team buildout?",
    answer: "Leadership team buildouts require careful confidentiality management because the process may involve assessing incumbent executives, running searches for roles that do not yet exist publicly, and managing multiple candidates who may know each other. We operate strict information barriers between search workstreams and manage all candidate communications personally.",
  }
];


const steps = [
  {
    num: "01",
    title: "Leadership Architecture",
    body: "Before any search begins, we map the full product leadership structure the business needs. What roles are required? In what sequence should they be hired? Where are the interdependencies — roles that need to be planned together to work together? Architecture first. Search second.",
  },
  {
    num: "02",
    title: "Value Creation Alignment",
    body: "Every role in the buildout is defined against the value creation plan. A CPO, Head of Product, VP Product, or Principal Product Manager brief is written differently depending on the thesis and stage of ownership. Every mandate reflects the investment thesis specifically.",
  },
  {
    num: "03",
    title: "Sequenced Search Programme",
    body: "We design a sequenced programme — which roles run simultaneously, which wait for an earlier placement to land, and how timelines are managed across the full buildout. This prevents the chaos of multiple simultaneous searches running independently.",
  },
  {
    num: "04",
    title: "Unified Assessment Framework",
    body: "Every candidate across the buildout is assessed using the same product-specific framework for VC and PE, calibrated for the business and stage of ownership. This produces consistent quality across the team — not a team shaped by whoever happened to be available.",
  },
  {
    num: "05",
    title: "Team Cohesion Consideration",
    body: "As the buildout progresses, we factor in team dynamics. A leadership team isn't a collection of outstanding individuals. It's a group that needs to operate together. We assess for complementarity as well as individual capability.",
  },
  {
    num: "06",
    title: "Coordinated Onboarding",
    body: "We stay engaged through the onboarding period across every placed executive. Where roles interact, we help manage the transition so the team is functional from day one — not still finding its feet three months in.",
  },
];

const outcomes = [
  "A coordinated search programme designed around your 100-day plan — not a series of disconnected mandates.",
  "Product leadership architecture built before search begins — so every hire is planned to work alongside the others.",
  "One point of accountability across the entire buildout.",
];

export default function LeadershipBuildoutsPage() {
  useFaqSchema(leadershipBuildoutsFaqs, "buildouts-faq-jsonld");
  useEffect(() => {
    const prev = document.title;
    document.title = "Product Leadership Team Buildouts for VC- and PE-Backed Companies | Alder Search";
    const meta = document.querySelector('meta[name="description"]');
    const prevContent = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "Coordinated product leadership team buildouts for platform acquisitions and high-growth VC- and PE-backed companies. Multiple product leaders placed against your 100-day plan."
    );
    return () => {
      document.title = prev;
      meta?.setAttribute("content", prevContent);
    };
  }, []);

  const handleEngageClick = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/#contact";
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.006 80)" }}>
      <Navigation />

      {/* HERO */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{ background: "linear-gradient(160deg, oklch(0.18 0.055 155) 0%, oklch(0.245 0.065 155) 100%)" }}
      >
        <div className="container relative z-10">
          <div className="mb-4 flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <Link href="/" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Home</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <Link href="/executive-search" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Executive Search</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <span style={{ color: "oklch(0.62 0.085 75)" }}>Product Leadership Team Buildouts</span>
          </div>
          <h1 className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 1.1, color: "oklch(0.97 0.006 80)", maxWidth: "760px" }}>
            Build the Product Leadership Team the Business Actually Needs.
          </h1>
          <p className="mb-10" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "clamp(1rem, 2vw, 1.15rem)", lineHeight: 1.75, color: "oklch(0.78 0.018 80)", maxWidth: "580px" }}>
            Platform acquisitions and high-growth portfolio companies need a product leadership layer built simultaneously. A single search at a time doesn't work at that pace. We plan and execute coordinated multi-role buildouts aligned to your value creation plan.
          </p>
          <div className="flex flex-wrap gap-4">
            <button onClick={handleEngageClick} className="px-8 py-3.5 transition-all duration-300"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
              Engage Alder Search
            </button>
            <Link href="/executive-search" className="px-8 py-3.5 transition-all duration-300"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.75rem", background: "transparent", color: "oklch(0.88 0.012 80)", border: "1px solid oklch(0.45 0.07 155)", borderRadius: "2px", textDecoration: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.62 0.085 75)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.45 0.07 155)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.012 80)"; }}>
              ← Back to Executive Search
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES STRIP */}
      <section className="py-14" style={{ background: "oklch(0.245 0.065 155)", borderBottom: "1px solid oklch(0.32 0.07 155)" }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((text, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="mt-1 shrink-0" style={{ width: "20px", height: "20px", borderRadius: "50%", background: "oklch(0.62 0.085 75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="oklch(0.18 0.055 155)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.65, color: "oklch(0.82 0.015 80)" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-24" style={{ background: "oklch(0.97 0.006 80)" }}>
        <div className="container">
          <div className="mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>How This Search Works</div>
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.245 0.065 155)", maxWidth: "640px" }}>
            A Coordinated Buildout — Not a Series of Individual Searches.
          </h2>
          <p className="mb-16" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)", maxWidth: "560px" }}>
            Most firms run product leadership buildouts as sequential individual searches. We run them as a coordinated programme. The difference shows in the cohesion of the team you end up with.
          </p>
          <div className="flex flex-col gap-0">
            {steps.map((step) => (
              <div key={step.num} className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 py-8" style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 400, fontSize: "0.72rem", letterSpacing: "0.1em", color: "oklch(0.62 0.085 75)", paddingTop: "3px" }}>{step.num}</div>
                <div>
                  <h3 className="mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: "1.05rem", color: "oklch(0.245 0.065 155)" }}>{step.title}</h3>
                  <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)" }}>{step.body}</p>
                </div>
              </div>
            ))}
            <div style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }} />
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-20" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="container">
          <div className="mx-auto" style={{ maxWidth: "680px", textAlign: "center" }}>
            <div className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: "clamp(1.15rem, 2.5vw, 1.55rem)", lineHeight: 1.6, color: "oklch(0.88 0.012 80)" }}>
              "A platform acquisition doesn't need six great individuals. It needs a leadership team. That's a different brief."
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>
              Ryan Ollerenshaw, Founding Partner, Alder Search
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section id="contact" className="py-24" style={{ background: "oklch(0.245 0.065 155)" }}>
        <div className="container text-center">
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.97 0.006 80)" }}>
            Planning a Platform Build or Post-Deal Restructure?
          </h2>
          <p className="mb-10 mx-auto" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.72 0.018 80)", maxWidth: "520px" }}>
            If you're facing a platform acquisition, a significant post-deal restructure, or a high-growth buildout — let's design the programme before search begins.
          </p>
          <button onClick={handleEngageClick} className="px-10 py-4 transition-all duration-300"
            style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
            Engage Alder Search
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
